import React, { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useU2000AuthContext } from "../context/auth/U2000UseAuthContext";
import { U2000MenuGetGlobalSearchItems, type U2000MenuItem } from "../menu/U2000MenuUtils";
import { useU2000LogicalUnitContext } from "../context/logicalUnit/U2000LogicalUnitContext";

const U2000SearchPlaceholder: React.FC = () => {
  const { t } = useTranslation();
  const { auth } = useU2000AuthContext();
  const navigate = useNavigate();
  const { setQuickSearchData } = useU2000LogicalUnitContext();

  const globalSearchItems: U2000MenuItem[] = useMemo(() => {
    return U2000MenuGetGlobalSearchItems((auth?.menu ?? []) as U2000MenuItem[]);
  }, [auth?.menu]);

  const [selectedFilter, setSelectedFilter] = useState<string | number | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);

  const globalSearchItemLabels = useMemo(() => {
    const labels = globalSearchItems
      .map((item) => (item.label ? t(item.label) : ""))
      .map((label) => label.trim())
      .filter(Boolean);

    const maxLabels = 5;
    const visibleLabels = labels.slice(0, maxLabels);
    const remainingCount = Math.max(0, labels.length - maxLabels);

    return { labels, visibleLabels, remainingCount };
  }, [globalSearchItems, t]);

  // Garder un filtre sélectionné valide quand la liste change (ex: changement d'organisation / rôles).
  useEffect(() => {
    if (globalSearchItems.length === 0) {
      setSelectedFilter(null);
      return;
    }

    const isSelectedStillValid = selectedFilter !== null && globalSearchItems.some((item) => item.id === selectedFilter);
    if (!isSelectedStillValid) {
      setSelectedFilter(globalSearchItems[0]?.id ?? null);
    }
  }, [globalSearchItems, selectedFilter]);

  const handleSearch = () => {
    if (!searchValue.trim()) {
      return;
    }

    const selectedItem = globalSearchItems.find((item) => item.id === selectedFilter);
    if (!selectedItem || !selectedItem.quickSearchFieldName || !selectedItem.link) {
      return;
    }

    // Stocker les données du quick search dans le contexte
    const condition = selectedItem.quickSearchCondition || "contains";
    setQuickSearchData({
      fieldName: selectedItem.quickSearchFieldName,
      condition: condition,
      value: searchValue.trim(),
      isFromQuickSearch: true,
      targetLogicalUnit: selectedItem.link, // Identifier l'unité logique cible
    });

    // Vider le champ de recherche après avoir lancé la recherche
    setSearchValue("");

    // Naviguer vers l'unité logique
    navigate(selectedItem.link);
    closeFocus();
  };

  const closeFocus = () => {
    setIsFocused(false);
  };

  // Gérer le click outside manuellement
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && event.target instanceof Node && !searchRef.current.contains(event.target) && isFocused) {
        setIsFocused(false);
      }
    }

    if (isFocused) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFocused]);

  const handleDivClick = () => {
    setIsFocused(true);
  };

  const handleOverlayClick = () => {
    setIsFocused(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    } else if (e.key === "Escape") {
      e.preventDefault();
      // Vider le champ et fermer le popup sans lancer la recherche
      setSearchValue("");
      closeFocus();
    }
  };

  // Si aucun filtre n'est disponible, ne pas afficher le Quick Search du tout.
  if (globalSearchItems.length === 0) {
    return null;
  }

  const placeholderSuffix =
    globalSearchItemLabels.visibleLabels.length > 0
      ? ` (${globalSearchItemLabels.visibleLabels.join(", ")}${globalSearchItemLabels.remainingCount > 0 ? ` ${t("lblOtherCount", { count: globalSearchItemLabels.remainingCount })}` : ""})`
      : "";

  // NOTE: le placeholder compact (header) reste la valeur initiale "Search..."
  // Le placeholder du dialog (input focus) est dynamique.
  const inputPlaceholder = `${t("phQuickSearchInputBase")}${placeholderSuffix}`;

  return (
    <div className="relative" ref={searchRef}>
      {/* État sans focus - div stylé comme un champ de recherche (référence de position) - aligné à droite */}
      <div
        className={`flex items-center w-[200px] h-10 pl-10 pr-4 bg-white border border-gray-300 rounded-md text-sm text-gray-500 cursor-text hover:border-gray-400 ml-auto ${
          isFocused ? "invisible" : ""
        }`}
        onClick={handleDivClick}
      >
        <div className="absolute left-3">
          <FaSearch className="text-gray-400 text-sm" />
        </div>
        <span>{t("phQuickSearchCollapsed")}</span>
      </div>

      {/* État avec focus - composante existante alignée à droite à partir du fake search */}
      {isFocused && (
        <>
          {/* Overlay cliquable */}
          <div className="fixed inset-0 bg-black/25 z-998" onClick={handleOverlayClick} />

          {/* Container unifié avec input + bouton + options - aligné à droite */}
          <div className="absolute top-0 right-0 bg-white border border-gray-300 rounded-lg p-4 w-[500px] shadow-lg z-1000" onClick={(e) => e.stopPropagation()}>
            {/* Search input avec bouton */}
            <div className="flex mb-4">
              <div className="relative flex-1">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <FaSearch className="text-blue-500 text-sm" />
                </div>
                <input
                  type="text"
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={inputPlaceholder}
                  className="w-full h-10 pl-10 pr-4 border border-gray-300 rounded-l-md text-sm text-gray-900 placeholder-gray-500 focus:outline-hidden"
                  autoFocus
                />
              </div>
              <button className="px-6 bg-blue-600 text-white rounded-r-md font-medium hover:bg-blue-700 focus:outline-hidden" onClick={handleSearch}>
                {t("btnSearch")}
              </button>
            </div>

            {/* Radio button filters */}
            <div className="grid grid-cols-2 gap-4">
              {globalSearchItems.map((item) => (
                <label key={String(item.id ?? item.link ?? item.label ?? "")} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="searchFilter"
                    value={String(item.id ?? "")}
                    checked={selectedFilter !== null && item.id === selectedFilter}
                    onChange={() => setSelectedFilter(item.id ?? null)}
                    className="mr-2 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{item.label ? t(item.label) : String(item.id ?? "")}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default U2000SearchPlaceholder;
